import { Capacitor } from '@capacitor/core';
import { AppLauncher } from '@capacitor/app-launcher';

export type MapPlatform = 'android' | 'ios' | 'desktop';

export interface MaybeCoordinates {
  latitude?: unknown;
  longitude?: unknown;
}

export interface MapTarget {
  latitude: number;
  longitude: number;
  /** Nom affiché sur le marqueur à la destination (nom du SmartTag). */
  label: string;
}

/**
 * Coordonnées valides pour ouvrir une carte : numériques, finies, dans les
 * bornes géographiques, et jamais (0,0) — sentinelle classique d'une position
 * GPS absente plutôt qu'une vraie position sur « Null Island ».
 */
export function isValidMapCoordinate(
  coords: MaybeCoordinates | null | undefined,
): coords is { latitude: number; longitude: number } {
  if (!coords) return false;
  const { latitude, longitude } = coords;
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return false;
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  if (latitude < -90 || latitude > 90) return false;
  if (longitude < -180 || longitude > 180) return false;
  if (latitude === 0 && longitude === 0) return false;
  return true;
}

/** Construit la cible « Ouvrir dans Maps », ou `null` si la position est absente/invalide. */
export function toMapTarget(
  point: MaybeCoordinates | null | undefined,
  label: string,
): MapTarget | null {
  if (!isValidMapCoordinate(point)) return null;
  return { latitude: point.latitude, longitude: point.longitude, label };
}

/**
 * Détecte l'OS cible : plateforme native Capacitor si l'app tourne dans le
 * shell (APK), sinon l'agent utilisateur du navigateur (web/PWA).
 */
export function detectMapPlatform(): MapPlatform {
  if (Capacitor.isNativePlatform()) {
    return Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
  }
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  // iPadOS 13+ se déclare comme Macintosh mais expose un écran tactile.
  const isTouchMac = /macintosh/i.test(ua) && (navigator?.maxTouchPoints ?? 0) > 1;
  return isTouchMac ? 'ios' : 'desktop';
}

/**
 * URLs cartographiques pour un point donné. Le format `q=lat,lng(label)`
 * (Android `geo:` et Google Maps HTTPS hérité) affiche un marqueur nommé
 * sans nécessiter de clé API Google Maps.
 */
export function buildMapUrls(target: MapTarget): { geo: string; googleMaps: string; appleMaps: string } {
  const { latitude, longitude, label } = target;
  const coords = `${latitude},${longitude}`;
  const encodedLabel = encodeURIComponent(label);
  return {
    geo: `geo:${coords}?q=${coords}(${encodedLabel})`,
    googleMaps: `https://www.google.com/maps?q=${coords}(${encodedLabel})`,
    appleMaps: `https://maps.apple.com/?ll=${coords}&q=${encodedLabel}`,
  };
}

/**
 * Ouvre la position dans l'application cartographique native — jamais de
 * calcul d'itinéraire ni de géolocalisation déclenchée, uniquement
 * l'affichage d'un marqueur à des coordonnées déjà reçues par l'app.
 *
 * Android natif tente une URI `geo:` (l'utilisateur choisit l'app installée)
 * puis retombe sur Google Maps HTTPS si aucune app ne la gère. iOS ouvre
 * Apple Maps. Le reste (navigateur desktop, Android/iOS en navigateur ou PWA
 * sans shell natif) ouvre Google Maps ou Apple Maps HTTPS via le même
 * mécanisme Capacitor (`AppLauncher`), qui retombe sur `window.open` hors
 * contexte natif.
 */
export async function openInNativeMaps(target: MapTarget): Promise<void> {
  const platform = detectMapPlatform();
  const urls = buildMapUrls(target);

  if (Capacitor.isNativePlatform() && platform === 'android') {
    try {
      const { completed } = await AppLauncher.openUrl({ url: urls.geo });
      if (completed) return;
    } catch {
      // Aucune app ne gère geo: sur cet appareil — repli HTTPS ci-dessous.
    }
    await AppLauncher.openUrl({ url: urls.googleMaps });
    return;
  }

  if (platform === 'ios') {
    await AppLauncher.openUrl({ url: urls.appleMaps });
    return;
  }

  await AppLauncher.openUrl({ url: urls.googleMaps });
}
